import type { State } from '../../../../../Flux/Slice/index.ts'

/**
 * Shared plumbing for the per-`(sourceId, …)` `createSelector` caches used by every cube
 * transformer (`Transformer/Impl/*`), `CubeContext`, and `SelectFilteredCube`.
 *
 * Two problems this solves at once:
 *  - **Duplication**: each module otherwise repeats the same `Map` + get/build/set dance.
 *  - **Leak**: those module-level Maps grew forever — a new selector instance per source and per
 *    zoomed path — and nothing cleared them on `visualization.reset()` (re-entering the workspace
 *    in the same SPA session). Every cache created here registers itself so `clearAllSelectorCaches()`
 *    can wipe them in one place (see CacheResetEpic).
 */
const registry = new Set<Map<string, unknown>>()

/** Create a self-registering selector cache. */
export function createSelectorCache<T>(): Map<string, (s: typeof State) => T> {
  const cache = new Map<string, (s: typeof State) => T>()
  registry.add(cache as Map<string, unknown>)
  return cache
}

/** Return the cached selector for `key`, building (and caching) it on first use. */
export function getOrBuildSelector<T>(
  cache: Map<string, (s: typeof State) => T>,
  key: string,
  build: () => (s: typeof State) => T
): (s: typeof State) => T {
  let sel = cache.get(key)
  if (!sel) {
    sel = build()
    cache.set(key, sel)
  }
  return sel
}

/** Drop every cached selector. Called on `visualization.reset()` to release per-session state. */
export function clearAllSelectorCaches(): void {
  for (const cache of registry) cache.clear()
}
